import { FieldError } from "react-hook-form";
import { intl } from "LocaleWrapper";

import { SvgIconTypeMap, Tooltip } from "@mui/material";
import WrongLocationOutlinedIcon from "@mui/icons-material/WrongLocationOutlined";
import { DefaultComponentProps } from "@mui/material/OverridableComponent";

import { ChildProps, ChildPropsFromArray, ChildPropsFromPagination } from "types/commons";
import { GetPushedBargeVoyagesQuery, GetPushingTugVoyageQuery } from "__generated__/view/pushingtug/graphql/pushingTug.queries.generated";

export type PushingTugVoyage = ChildProps<GetPushingTugVoyageQuery, "voyage">;
export type PushedBargeVoyage = ChildPropsFromPagination<GetPushedBargeVoyagesQuery, "pushedBargeVoyages">;

export type PushedBargeCall = ChildPropsFromArray<PushedBargeVoyage, "calls">
export type PushingTugCall = ChildPropsFromArray<PushingTugVoyage, "calls">

export enum PushingTugPlanningErrorType {
	MISMATCHING_LOCATION = "TUG_AND_BARGE_CALL_LOCATION_MISMATCH"
}

export const getPreliminaryLinkedCalls = (
	pushedBargeCalls: readonly PushedBargeCall[],
	pushingTugCalls: readonly PushingTugCall[],
) => {
	const used: Set<number> = new Set();

	return pushingTugCalls.map(pushingTugCall => {
		const candidate = pushedBargeCalls
			.filter((pushedBargeCall) => pushedBargeCall.location.id === pushingTugCall.location.id && !used.has(pushedBargeCall.id))
			.map(it => ({
				call: it,
				diff: it.pta && pushingTugCall.pta
					? Math.abs(new Date(it.pta).getTime() - new Date(pushingTugCall.pta).getTime())
					: Number.MAX_SAFE_INTEGER,
			}))
			.sort((a, b) => a.diff - b.diff)
			.at(0);

		if (candidate) {
			used.add(candidate.call.id);
		}
		return { pushingTugCall, pushedBargeCall: candidate?.call ?? null };
	});
};

const iconProps: DefaultComponentProps<SvgIconTypeMap> = {
	color: "error",
	fontSize: "small",
};

const pushingTugPlanningErrorIcons = {
	[PushingTugPlanningErrorType.MISMATCHING_LOCATION]: <WrongLocationOutlinedIcon {...iconProps} />,
};

export const getPushingTugPlanningErrors = (linkErrors: FieldError[]) =>
	[ ...new Set(
		linkErrors
			.map(error => error.message)
			.filter((message): message is PushingTugPlanningErrorType => !!message),
	) ].map(error => (
		<Tooltip key={error} title={intl.formatMessage({ id: `PUSHING_TUG_PLANNING_ERROR_${error}` })}>
			{pushingTugPlanningErrorIcons[error]}
		</Tooltip>
	));
